import { MatchingProfilePreviewCard } from "@/components/mentor-profile/matching-profile-preview-card";
import { PhoneFrame } from "@/components/mentor-profile/phone-frame";
import { WizardBackground } from "@/components/mentor-profile/wizard-background";
import { Wordmark } from "@/components/brand/wordmark";
import {
  useSubmitMentorProfile,
  type MentorProfileChangeRequest,
  type MentorProfileDraft,
} from "@/lib/api/mentor-profile";
import type { DraftSaveState } from "@/lib/hooks/mentor-profile/use-mentor-profile-draft";
import { isReviewSection } from "@/lib/mentor-profile/profile-sections";
import { isSectionUpdated } from "@/lib/mentor-profile/revision-change-detection";
import { canSubmitProfile } from "@/lib/mentor-profile/submit-readiness";
import { cn } from "@/lib/utils";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { Moodies } from "@somethings-ui/core/assets";
import { Button } from "@somethings-ui/web";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";

interface ReviewStepProps {
  draft: MentorProfileDraft;
  saveState: DraftSaveState;
  onKeepEditing: () => void;
  onSubmitted: () => void;
  changeRequests?: MentorProfileChangeRequest[];
  baselineDraft?: MentorProfileDraft;
  reviewUpdatedAt?: string | null;
  mentorId?: string;
}

export function ReviewStep({
  draft,
  saveState,
  onKeepEditing,
  onSubmitted,
  changeRequests = [],
  baselineDraft,
  reviewUpdatedAt,
  mentorId,
}: ReviewStepProps) {
  const submit = useSubmitMentorProfile();
  const { track } = useTrackEvent();
  const [error, setError] = useState<string | null>(null);

  const openRequests = useMemo(
    () => changeRequests.filter((c) => c.status === "open"),
    [changeRequests],
  );
  const revisionsReady = useMemo(() => {
    if (openRequests.length === 0) return true;
    return canSubmitProfile({
      status: "changes_requested",
      draft,
      publishedSnapshot: null,
      baseline: baselineDraft,
      changeRequests: openRequests,
      reviewUpdatedAt,
    });
  }, [baselineDraft, draft, openRequests, reviewUpdatedAt]);

  const handleSubmit = async () => {
    setError(null);
    try {
      await submit.mutateAsync();
      const props = { mentor_id: mentorId };
      if (openRequests.length > 0) {
        track(PostHogEvents.MENTOR_PROFILE_REVISION_SUBMITTED, {
          ...props,
          review_round: new Set(openRequests.map((r) => r.created_at)).size || 1,
        });
      } else {
        track(PostHogEvents.MENTOR_PROFILE_SUBMITTED, props);
      }
      onSubmitted();
    } catch {
      setError("We couldn't submit your profile. Please try again.");
    }
  };

  return (
    <WizardBackground className="items-start justify-center gap-12">
      <div className="relative z-10 flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-surfaces-z5 bg-surfaces-z0 shadow-[0px_8px_48px_0px_rgba(0,0,0,0.15)]">
        <header className="border-b border-surfaces-z5 px-8 pb-6 pt-4">
          <div className="flex items-end justify-between">
            <Wordmark className="h-[18px] text-black" />
            <span className="rounded-full bg-surfaces-z3 px-2.5 py-0.5 text-xs font-medium text-text-primary">
              Draft
            </span>
          </div>
        </header>

        <div className="flex flex-col px-8 py-10">
          <div className="flex size-14 items-center justify-center rounded-2xl bg-brand-ecru/60">
            <img
              src={Moodies.love}
              alt=""
              className="size-10 object-contain"
            />
          </div>

          <h1 className="mt-4 font-heading text-2xl font-bold leading-[1.2] tracking-[-0.48px] text-text-primary">
            {openRequests.length > 0
              ? "Ready to resubmit?"
              : "One last look – ready to send it in?"}
          </h1>
          <p className="mt-2 font-body text-[15px] leading-relaxed text-text-secondary">
            {openRequests.length > 0
              ? "Make sure every flagged section has a real change before sending it back."
              : "A member of our team gives every profile a quick review before you go live."}
          </p>

          {openRequests.length > 0 ? (
            <ul className="mt-5 space-y-2">
              {openRequests.map((request) => {
                const updated =
                  !!baselineDraft &&
                  isReviewSection(request.section) &&
                  isSectionUpdated(draft, baselineDraft, request.section);
                return (
                  <li
                    key={`${request.section}-${request.created_at}`}
                    className={cn(
                      "rounded-lg border px-4 py-3",
                      updated
                        ? "border-brand-periwinkle/40 bg-[#f3f5ff]"
                        : "border-brand-tangelo/30 bg-[#fff8f4]",
                    )}
                  >
                    <p
                      className={cn(
                        "font-body text-xs font-semibold uppercase tracking-[0.3px]",
                        updated ? "text-brand-periwinkle" : "text-brand-tangelo",
                      )}
                    >
                      {updated ? "Updated" : "Still needs changes"}
                    </p>
                    <p className="mt-1 font-body text-sm text-text-primary">
                      {request.section}
                    </p>
                  </li>
                );
              })}
              {!revisionsReady ? (
                <p className="font-body text-xs text-text-secondary">
                  Update each flagged section before resubmitting.
                </p>
              ) : null}
            </ul>
          ) : null}

          <div className="flex h-5 items-center gap-2 pt-6 text-xs text-text-secondary">
            {saveState === "saving" ? (
              <>
                <Loader2 className="size-3.5 animate-spin" />
                Saving...
              </>
            ) : saveState === "saved" ? (
              <span>All changes saved</span>
            ) : null}
          </div>

          {error ? (
            <span className="pt-2 font-body text-sm text-states-error">
              {error}
            </span>
          ) : null}

          <div className="flex flex-wrap items-center gap-6 pt-8">
            <Button
              size="sm"
              fullWidth={false}
              onPress={() => void handleSubmit()}
              disabled={submit.isPending || !revisionsReady}
              loading={submit.isPending}
            >
              {openRequests.length > 0
                ? "Resubmit for review"
                : "Submit for review"}
            </Button>
            <Button
              size="sm"
              variant="quaternary"
              fullWidth={false}
              disabled={submit.isPending}
              leadingIcon={<ArrowLeft className="size-4" />}
              onPress={onKeepEditing}
            >
              Keep editing
            </Button>
          </div>
        </div>
      </div>

      <div className="relative z-10 flex shrink-0 flex-col items-center gap-3 self-start">
        <span className="text-xs text-[#797774]">
          Live preview · what teens see
        </span>
        <PhoneFrame>
          <MatchingProfilePreviewCard draft={draft} />
        </PhoneFrame>
      </div>
    </WizardBackground>
  );
}
